import React, { useState } from 'react';
import { 
  useGetPaymentsQuery,
  useApprovePaymentMutation,
  useRejectPaymentMutation
} from '../../features/payments/paymentApi';
import { 
  FiCheck, 
  FiX, 
  FiRefreshCw, 
  FiChevronDown, 
  FiChevronUp, 
  FiArrowLeft, 
  FiArrowRight,
  FiSearch,
  FiUser,
  FiMail,
  FiDollarSign
} from 'react-icons/fi';
import Swal from 'sweetalert2';

const PaymentStatusBadge = ({ status, reason }) => {
  const statusConfig = {
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '🕒' },
    completed: { bg: 'bg-green-100', text: 'text-green-800', icon: '✓' },
    rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: '✗' }
  };

  return (
    <div className={`${statusConfig[status].bg} ${statusConfig[status].text} px-3 py-1 rounded-full text-xs font-medium inline-flex items-center`}>
      <span className="mr-1">{statusConfig[status].icon}</span>
      {status.toUpperCase()}
      {reason && (
        <span className="ml-1 text-xs">({reason})</span>
      )}
    </div>
  );
};

const UserInfoCard = ({ name, email, balance, amount }) => (
  <div className="bg-white p-3 rounded-md shadow-xs border border-gray-100">
    <div className="flex items-center mb-2">
      <FiUser className="text-gray-500 mr-2" />
      <span className="text-sm font-medium">{name}</span>
    </div>
    <div className="flex items-center mb-2">
      <FiMail className="text-gray-500 mr-2" />
      <span className="text-sm text-gray-600 break-all">{email}</span>
    </div>
    <div className="flex items-center mb-2">
      <FiDollarSign className="text-gray-500 mr-2" />
      <span className="text-sm font-medium">Amount: ₹{amount.toLocaleString()}</span>
    </div>
    <div className="flex items-center">
      <FiDollarSign className="text-gray-500 mr-2" />
      <span className="text-sm font-medium">Balance: ₹{balance.toFixed(2)}</span>
    </div>
  </div>
);

const TransactionRow = ({ payment, onApprove, onReject }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  return (
    <>
      {/* Desktop Row */}
      <tr className="hidden md:table-row hover:bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
          {payment.utrNumber}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          <div className="font-medium">{payment.name}</div>
          <div className="text-xs text-gray-400">{payment.email}</div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          ₹{payment.amount.toLocaleString()}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          ₹{payment.accountBalance.toFixed(2)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {payment.bankName} (****{payment.accountNumber.slice(-4)})
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {new Date(payment.paymentDate).toLocaleDateString()}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <PaymentStatusBadge status={payment.status} reason={payment.rejectionReason} />
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
          {payment.status === 'pending' && (
            <div className="flex space-x-2">
              <button
                onClick={() => onApprove(payment.id)}
                className="text-green-600 hover:text-green-800 p-2 rounded-full hover:bg-green-50 transition-colors"
                title="Approve"
              >
                <FiCheck size={18} />
              </button>
              <button
                onClick={() => onReject(payment.id)}
                className="text-red-600 hover:text-red-800 p-2 rounded-full hover:bg-red-50 transition-colors"
                title="Reject"
              >
                <FiX size={18} />
              </button>
            </div>
          )}
        </td>
      </tr>

      {/* Mobile Row */}
      <tr className="md:hidden border-b border-gray-200">
        <td className="px-4 py-3" colSpan="6">
          <div 
            className="flex justify-between items-center cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div>
              <div className="font-medium text-gray-900">{payment.utrNumber}</div>
              <div className="text-sm text-gray-500">{payment.name}</div>
              <div className="text-xs text-gray-400">
                Amount: ₹{payment.amount.toLocaleString()} | Balance: ₹{payment.accountBalance.toFixed(2)}
              </div>
            </div>
            <div className="flex items-center">
              <PaymentStatusBadge status={payment.status} />
              {isExpanded ? <FiChevronUp className="ml-2" /> : <FiChevronDown className="ml-2" />}
            </div>
          </div>

          {isExpanded && (
            <div className="mt-2 pl-2 space-y-2 text-sm">
              <UserInfoCard 
                name={payment.name} 
                email={payment.email} 
                balance={payment.accountBalance}
                amount={payment.amount}
              />
              <div>
                <span className="font-medium">Bank:</span> {payment.bankName} (****{payment.accountNumber.slice(-4)})
              </div>
              <div>
                <span className="font-medium">Date:</span> {new Date(payment.paymentDate).toLocaleDateString()}
              </div>
              {payment.rejectionReason && (
                <div>
                  <span className="font-medium">Reason:</span> {payment.rejectionReason}
                </div>
              )}
              {payment.status === 'pending' && (
                <div className="flex space-x-2 pt-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onApprove(payment.id);
                    }}
                    className="flex-1 bg-green-600 text-white py-1 px-3 rounded flex items-center justify-center"
                  >
                    <FiCheck className="mr-1" /> Approve
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onReject(payment.id);
                    }}
                    className="flex-1 bg-red-600 text-white py-1 px-3 rounded flex items-center justify-center"
                  >
                    <FiX className="mr-1" /> Reject
                  </button>
                </div>
              )}
            </div>
          )}
        </td>
      </tr>
    </>
  );
};

const Transactions = () => {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const limit = 10;
  
  const { 
    data: response, 
    isLoading, 
    isError, 
    refetch 
  } = useGetPaymentsQuery({ page, limit, search: searchQuery });
  
  const [approvePayment] = useApprovePaymentMutation();
  const [rejectPayment] = useRejectPaymentMutation();

  const handleApprove = async (paymentId) => {
    try {
      await approvePayment(paymentId).unwrap();
      Swal.fire({
        position: 'center',
        icon: 'success',
        title: 'Payment Approved',
        showConfirmButton: false,
        timer: 1500,
        backdrop: `
          rgba(0,0,0,0.5)
          url("/images/checkmark.gif")
          center top
          no-repeat
        `
      });
    } catch (error) {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: 'Approval Failed',
        text: error.data?.message || 'Please try again',
        confirmButtonColor: '#3085d6',
      });
    }
  };

  const handleReject = async (paymentId) => {
    const { value: reason } = await Swal.fire({
      title: 'Rejection Reason',
      input: 'textarea',
      inputPlaceholder: 'Enter detailed reason for rejection...',
      inputAttributes: {
        'aria-label': 'Type your rejection reason here'
      },
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Confirm Rejection',
      inputValidator: (value) => {
        if (!value) return 'You need to provide a reason!';
        if (value.length < 10) return 'Reason must be at least 10 characters!';
      }
    });

    if (reason) {
      try {
        await rejectPayment({ paymentId, rejectionReason: reason }).unwrap();
        Swal.fire({
          position: 'center',
          icon: 'success',
          title: 'Payment Rejected',
          text: 'The payment has been successfully rejected',
          showConfirmButton: false,
          timer: 1500
        });
      } catch (error) {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: 'Rejection Failed',
          text: error.data?.message || 'Please try again',
          confirmButtonColor: '#3085d6',
        });
      }
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1); // Reset to first page when searching
    refetch();
  };

  if (isLoading) return (
    <div className="flex justify-center items-center min-h-[300px]">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );

  if (isError) return (
    <div className="bg-red-50 border-l-4 border-red-500 p-4 my-6 rounded-r">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-red-700">
            Failed to load payments. 
            <button 
              onClick={refetch}
              className="ml-2 text-blue-600 hover:text-blue-800 font-medium"
            >
              Click to retry
            </button>
          </p>
        </div>
      </div>
    </div>
  );

  const { payments, pagination } = response || { payments: [], pagination: {} };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-semibold text-gray-800 mb-2 sm:mb-0">Payment Transactions</h2>
        <div className="flex items-center space-x-2">
          <form onSubmit={handleSearch} className="flex">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by UTR, Name, Email..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button
              type="submit"
              className="ml-2 inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg hover:from-blue-700 hover:to-blue-600 transition-all shadow-sm"
            >
              Search
            </button>
          </form>
          <button
            onClick={() => {
              setSearchQuery('');
              refetch();
            }}
            className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all shadow-sm"
          >
            <FiRefreshCw className="mr-2" />
            Reset
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 hidden md:table-header-group">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">UTR Number</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Details</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {payments?.map((payment) => (
              <TransactionRow 
                key={payment.id} 
                payment={payment} 
                onApprove={handleApprove}
                onReject={handleReject}
              />
            ))}
          </tbody>
        </table>
      </div>

      {payments?.length === 0 && (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No payments found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchQuery 
              ? `No transactions match your search for "${searchQuery}"`
              : 'There are currently no payment transactions to display.'}
          </p>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Clear search
            </button>
          )}
        </div>
      )}

      {pagination?.totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between">
          <div className="text-sm text-gray-500 mb-2 sm:mb-0">
            Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{' '}
            <span className="font-medium">{Math.min(page * limit, pagination.totalRecords)}</span> of{' '}
            <span className="font-medium">{pagination.totalRecords}</span> results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className={`px-3 py-1 rounded-md ${page === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
            >
              <FiArrowLeft className="inline mr-1" /> Previous
            </button>
            <div className="flex items-center px-3">
              <span className="text-sm text-gray-700">
                Page {page} of {pagination.totalPages}
              </span>
            </div>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page === pagination.totalPages}
              className={`px-3 py-1 rounded-md ${page === pagination.totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
            >
              Next <FiArrowRight className="inline ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;